import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, Download, Share2, BookOpen, Info } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const DemissionChomageSimulator = () => {
  const [inputs, setInputs] = useState({
    salaireBrutMensuel: '3000',
    anneesExperience: '12',
    projet: 'creation',
    formationCertifiante: true
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const salaire = parseFloat(inputs.salaireBrutMensuel) || 0;
    const annees = parseFloat(inputs.anneesExperience) || 0;
    const eligible = annees >= 10;

    const sjr = (salaire * 12) / 365;
    const formule1 = sjr * 0.404 + 13.18;
    const formule2 = sjr * 0.57;
    let areJournaliere = Math.max(formule1, formule2);
    areJournaliere = Math.min(areJournaliere, sjr * 0.75);
    areJournaliere = Math.max(areJournaliere, 32.13);
    const areMensuelle = areJournaliere * 30;

    // Durée : 6 mois de base, extensible à 12 mois si formation métier en pénurie
    const dureeMois = inputs.formationCertifiante ? 12 : 6;
    const droitsTotaux = areMensuelle * dureeMois;

    setResults({
      eligible,
      sjr: sjr.toFixed(2),
      areJournaliere: areJournaliere.toFixed(2),
      areMensuelle: Math.round(areMensuelle),
      dureeMois,
      droitsTotaux: Math.round(droitsTotaux),
      conditionsManquantes: !eligible ? `Il vous manque ${10 - annees} ans d'expérience (min. 10 ans requis)` : null
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Démission + Chômage - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Démission + Chômage', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`Éligible : ${results.eligible ? 'OUI' : 'NON'}`, 15, y); y += 7;
    doc.text(`ARE mensuelle estimée : ${results.areMensuelle?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Durée : ${results.dureeMois} mois`, 15, y); y += 7;
    doc.text(`Total : ${results.droitsTotaux?.toLocaleString()} EUR`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : Réforme assurance chômage mars 2026, Code du travail', 15, y);
    doc.save('simulation-demission-chomage-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Démission + Chômage
            </CardTitle>
            <CardDescription>Estimez vos droits au chômage après démission pour reconversion</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Salaire brut mensuel actuel (€)</Label>
              <Input data-testid="salaire-input" type="text" inputMode="numeric" value={inputs.salaireBrutMensuel}
                onChange={(e) => handleInputChange('salaireBrutMensuel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="3000" className="mt-1" />
            </div>
            <div>
              <Label>Années d'expérience professionnelle</Label>
              <Input data-testid="experience-input" type="text" inputMode="numeric" value={inputs.anneesExperience}
                onChange={(e) => handleInputChange('anneesExperience', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="12" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">Minimum 10 ans requis pour la démission-reconversion</p>
            </div>
            <div>
              <Label>Type de projet</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button data-testid="projet-creation-btn"
                  variant={inputs.projet === 'creation' ? 'default' : 'outline'}
                  className={inputs.projet === 'creation' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('projet', 'creation')}>Création d'entreprise</Button>
                <Button data-testid="projet-formation-btn"
                  variant={inputs.projet === 'formation' ? 'default' : 'outline'}
                  className={inputs.projet === 'formation' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('projet', 'formation')}>Formation qualifiante</Button>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="formation-toggle">
              <input type="checkbox" checked={inputs.formationCertifiante}
                onChange={(e) => handleInputChange('formationCertifiante', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm font-medium">Formation certifiante métier en pénurie (+6 mois)</span>
            </label>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Réforme assurance chômage - Mars 2026</li>
                    <li>10 ans d'activité minimum (3 120 jours)</li>
                    <li>Durée : 6 mois (extensible à 12 si formation pénurie)</li>
                    <li>1 seule demande par carrière</li>
                    <li>Accompagnement CEP obligatoire + validation commission</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Résultats</CardTitle>
            <Badge className={results.eligible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
              {results.eligible ? 'Éligible' : 'Non éligible'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            {!results.eligible && (
              <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200">
                <p className="text-sm font-semibold text-red-800">{results.conditionsManquantes}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">SJR</p>
                <p className="text-lg font-bold text-blue-900">{results.sjr}€</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">ARE journalière</p>
                <p className="text-lg font-bold text-blue-900">{results.areJournaliere}€</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-sm text-gray-600 mb-1">ARE mensuelle estimée</p>
              <p className="text-3xl font-black text-green-900" data-testid="result-are">{results.areMensuelle?.toLocaleString()}€</p>
              <p className="text-xs text-gray-500">pendant {results.dureeMois} mois</p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-sm text-purple-600 mb-1">Droits totaux estimés</p>
              <p className="text-2xl font-bold text-purple-900" data-testid="result-total">{results.droitsTotaux?.toLocaleString()}€</p>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm font-semibold text-amber-800 mb-2">Étapes obligatoires :</p>
              <ol className="list-decimal list-inside text-xs text-amber-700 space-y-1">
                <li>Accompagnement par un Conseil en Évolution Professionnelle (CEP)</li>
                <li>Validation du projet par la commission paritaire régionale</li>
                <li>Inscription à France Travail</li>
                <li>Dépôt du dossier ARE</li>
              </ol>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : Réforme mars 2026, Code du travail, Unédic</p>
              <p>francetravail.org | service-public.fr | centre-inffo.fr</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DemissionChomageSimulator;
